import React, { useState, useEffect, useMemo } from 'react';
import { useTwin } from '../context/TwinContext';
import { alertStore } from '../utils/alertStore';
import './Entities.css';

/* ── demo fallback ──────────────────────────────────────────── */

const DEMO_ENTITIES = [
  { id: 'Mixer_3_3218c6d8',           name: 'Mixer_3',               type: 'Mixer',     status: 'ACTIVE' },
  { id: 'Mixer_11_67785f3f',          name: 'Mixer_11',              type: 'Mixer',     status: 'ACTIVE' },
  { id: 'WaterTank_ab5e8bc0',         name: 'WaterTank',             type: 'Tank',      status: 'ACTIVE' },
  { id: 'FREEZER_TUNNEL_e12e0733',    name: 'FREEZER_TUNNEL (1)',    type: 'Freezer',   status: 'ACTIVE' },
  { id: 'FREEZER_TUNNEL_c726a21e',    name: 'FREEZER_TUNNEL (2)',    type: 'Freezer',   status: 'ACTIVE' },
  { id: 'BOX_SEALER_ad434a34',        name: 'BOX_SEALER',            type: 'Packaging', status: 'ACTIVE' },
  { id: 'BOX_ERECTOR_170c5e21',       name: 'BOX_ERECTOR',           type: 'Packaging', status: 'ACTIVE' },
  { id: 'COOKIE_FORMER_e87acad2',     name: 'COOKIE_FORMER',         type: 'Former',    status: 'ACTIVE' },
  { id: 'COOKIE_LINE_1_70cde09c',     name: 'COOKIE_LINE_1',         type: 'Line',      status: 'ACTIVE' },
  { id: 'COOKIE_LINE_2_31be44be',     name: 'COOKIE_LINE_2',         type: 'Line',      status: 'ACTIVE' },
  { id: 'CONVEYOR_STRIGHT_9c62c546',  name: 'CONVEYOR_STRAIGHT (1)', type: 'Conveyor',  status: 'ACTIVE' },
  { id: 'CONVEYOR_STRIGHT_a1f90af8',  name: 'CONVEYOR_STRAIGHT (2)', type: 'Conveyor',  status: 'ACTIVE' },
  { id: 'VERTICAL_CONVEYOR_a10ee21c', name: 'VERTICAL_CONVEYOR (1)', type: 'Conveyor',  status: 'ACTIVE' },
  { id: 'LABELING_BELT_5f98ffd2',     name: 'LABELING_BELT',         type: 'Belt',      status: 'ACTIVE' },
  { id: 'PLASTIC_LINER_23df2a72',     name: 'PLASTIC_LINER',         type: 'Liner',     status: 'ACTIVE' },
  { id: 'Camera_WaterTank_01',        name: 'Camera_WaterTank_01',   type: 'Camera',    status: 'ERROR'  },
  { id: 'Camera_Mixer_Area_01',       name: 'Camera_Mixer_Area_01',  type: 'Camera',    status: 'ERROR'  },
  { id: 'Factory_aa3d7d8b',           name: 'Factory',               type: 'Space',     status: 'ACTIVE' },
  { id: 'Warehouse_058779af',         name: 'Warehouse',             type: 'Space',     status: 'ACTIVE' },
];

const TYPE_ICON: Record<string, string> = {
  mixer: '⚙️', tank: '💧', freezer: '❄️', packaging: '📦', former: '🍪',
  line: '🔗', conveyor: '🔄', belt: '🏷️', liner: '🧻', camera: '📹',
  space: '🏢', robot: '🤖', pump: '🔵', press: '⚡', reactor: '🔬',
  vision: '👁️', turbine: '💨', generator: '⚡', compressor: '🌀',
};

function getIcon(name: string, type: string): string {
  const t = type.toLowerCase();
  if (TYPE_ICON[t]) return TYPE_ICON[t];
  const n = name.toLowerCase();
  for (const [key, icon] of Object.entries(TYPE_ICON)) {
    if (n.includes(key)) return icon;
  }
  return '🏭';
}

function sensorStatus(value: number, min: number, max: number): 'ok' | 'warning' | 'critical' {
  const range = max - min;
  if (range === 0) return 'ok';
  if (value > min + range * 0.95 || value < min) return 'critical';
  if (value > min + range * 0.8) return 'warning';
  return 'ok';
}

function pct(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
}

/* ── component ──────────────────────────────────────────────── */

const Entities: React.FC = () => {
  const { getActiveTwin } = useTwin();
  const activeTwin = getActiveTwin();

  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState<'ALL' | 'ACTIVE' | 'WARNING' | 'FAULT'>('ALL');
  const [expanded, setExpanded]   = useState<Set<string>>(new Set());
  const [view, setView]           = useState<'hierarchy' | 'table'>('hierarchy');

  const demoMode = !activeTwin || activeTwin.id === 'demo-cookie-factory';
  const machines = useMemo(() => activeTwin?.machines || [], [activeTwin]);
  const simData  = useMemo(() => activeTwin?.simulatedMachineData || {}, [activeTwin]);

  // Subscribe to alertStore so EWMA badges update live
  const [alertTick, setAlertTick] = useState(0);
  useEffect(() => {
    const unsub = alertStore.subscribe(() => setAlertTick(t => t + 1));
    return () => { unsub(); };
  }, []);

  const ewmaAlerts = useMemo(() => {
    const map: Record<string, string> = {};
    alertStore.getAlerts().forEach(a => {
      if (a.source === 'ewma' && a.stage) {
        const machineId = a.id.replace('fused-', '');
        map[machineId] = a.stage;
      }
    });
    return map;
  }, [alertTick]);

  /* ── build entity data ────────────────────────────────────── */

  const machineEntities = useMemo(() => {
    return machines.map(m => {
      const sensors = m.sensors.map(s => {
        const val = simData[m.machineId]?.[s.sensorId] ?? s.value;
        const st  = sensorStatus(val, s.min, s.max);
        return { ...s, liveValue: val, status: st, pct: pct(val, s.min, s.max) };
      });
      const critCount = sensors.filter(s => s.status === 'critical').length;
      const warnCount = sensors.filter(s => s.status === 'warning').length;
      const ewmaStage = ewmaAlerts[m.machineId];
      const machineStatus = ewmaStage === 'LATE' ? 'FAULT'
        : ewmaStage === 'MID' ? 'WARNING'
        : ewmaStage === 'EARLY' ? 'WARNING'
        : m.status === 'fault' ? 'FAULT'
        : critCount > 0 ? 'WARNING'
        : m.status === 'running' ? 'ACTIVE' : 'ACTIVE';
      return { ...m, sensors, critCount, warnCount, machineStatus, ewmaStage };
    });
  }, [machines, simData]);

  /* ── filter + search ──────────────────────────────────────── */

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return machineEntities.filter(m => {
      const matchSearch = !q || m.name.toLowerCase().includes(q) ||
        m.sensors.some(s => s.name.toLowerCase().includes(q) || s.type.toLowerCase().includes(q));
      const matchFilter = filter === 'ALL' ||
        (filter === 'ACTIVE' && m.machineStatus === 'ACTIVE') ||
        (filter === 'WARNING' && (m.warnCount > 0 || m.critCount > 0)) ||
        (filter === 'FAULT' && (m.machineStatus === 'FAULT' || m.status === 'fault'));
      return matchSearch && matchFilter;
    });
  }, [machineEntities, search, filter]);

  /* ── KPIs ─────────────────────────────────────────────────── */

  const totalSensors = machines.reduce((n, m) => n + m.sensors.length, 0);
  const totalCrit    = machineEntities.reduce((n, m) => n + m.critCount, 0);
  const totalWarn    = machineEntities.reduce((n, m) => n + m.warnCount, 0);
  const faultCount   = machines.filter(m => m.status === 'fault').length;
  const runningCount = machines.filter(m => m.status === 'running').length;

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const expandAll   = () => setExpanded(new Set(machineEntities.map(m => m.machineId)));
  const collapseAll = () => setExpanded(new Set());

  /* ── demo fallback view ───────────────────────────────────── */

  if (demoMode) {
    return (
      <div className="entities-page">
        <div className="ent-empty">
          <div className="ent-empty-icon">📦</div>
          <h2>No Active Twin</h2>
          <p>Upload a 3D model and configure your digital twin to see factory entities here.</p>
        </div>
      </div>
    );
  }

  /* ── real twin view ───────────────────────────────────────── */

  if (machines.length === 0) {
    return (
      <div className="entities-page">
        <div className="ent-empty">
          <div className="ent-empty-icon">📦</div>
          <h2>No Entities</h2>
          <p>Your twin has no machines configured. Create a twin with machines to see entities here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="entities-page">
      {/* Header */}
      <div className="ent-header">
        <div className="ent-header-left">
          <h1>📦 Factory Entities</h1>
          <span className="ent-twin-badge">
            {activeTwin?.factoryType === 'cookie' ? '🍪' : '🏭'} {activeTwin?.company}
          </span>
        </div>
        <div className="ent-header-right">
          <div className="ent-view-toggle">
            <button className={view === 'hierarchy' ? 'active' : ''} onClick={() => setView('hierarchy')}>☰ Cards</button>
            <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>▤ Table</button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="ent-kpis">
        {[
          { label: 'Machines',  value: machines.length, icon: '⚙️', color: '#a78bfa' },
          { label: 'Sensors',   value: totalSensors,    icon: '📡', color: '#00d4ff' },
          { label: 'Running',   value: runningCount,    icon: '🟢', color: '#34d399' },
          { label: 'Faults',    value: faultCount,      icon: '🔴', color: '#f87171' },
          { label: 'Critical',  value: totalCrit,       icon: '⚠️', color: '#f97316' },
          { label: 'Warnings',  value: totalWarn,       icon: '🟡', color: '#fbbf24' },
          { label: 'Health',    value: `${activeTwin?.healthScore ?? 100}%`, icon: '💚', color: (activeTwin?.healthScore ?? 100) >= 80 ? '#34d399' : '#fbbf24' },
        ].map((k, i) => (
          <div className="ent-kpi" key={i}>
            <span className="ek-icon">{k.icon}</span>
            <span className="ek-value" style={{ color: k.color }}>{k.value}</span>
            <span className="ek-label">{k.label}</span>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="ent-controls">
        <input className="ent-search" placeholder="🔍 Search machines & sensors..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="ent-filters">
          {(['ALL', 'ACTIVE', 'WARNING', 'FAULT'] as const).map(f => (
            <button key={f} className={`ent-filter ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'ALL' ? `All (${machineEntities.length})` : f === 'WARNING' ? `⚠ Alerts (${machineEntities.filter(m => m.warnCount + m.critCount > 0).length})` : f}
            </button>
          ))}
        </div>
        <div className="ent-expand-btns">
          <button onClick={expandAll}>Expand All</button>
          <button onClick={collapseAll}>Collapse All</button>
        </div>
      </div>

      {/* Hierarchy view */}
      {view === 'hierarchy' && (
        <div className="ent-machines">
          {filtered.map(m => {
            const isOpen = expanded.has(m.machineId);
            const statusColor = m.machineStatus === 'FAULT' ? '#f87171' : m.critCount > 0 ? '#f97316' : m.warnCount > 0 ? '#fbbf24' : '#34d399';
            return (
              <div className={`ent-machine-card ${isOpen ? 'open' : ''}`} key={m.machineId}
                style={{ '--m-color': statusColor } as React.CSSProperties}>
                <div className="emc-header" onClick={() => toggle(m.machineId)}>
                  <div className="emc-left">
                    <span className="emc-icon">{getIcon(m.name, m.type)}</span>
                    <div className="emc-title">
                      <span className="emc-name">{m.name}</span>
                      <span className="emc-type">{m.type}</span>
                    </div>
                  </div>
                  <div className="emc-badges">
                    {m.ewmaStage && (
                      <span className="emc-badge" style={{
                        background: m.ewmaStage === 'LATE' ? '#f8717133' : m.ewmaStage === 'MID' ? '#f9731633' : '#fbbf2433',
                        color:      m.ewmaStage === 'LATE' ? '#f87171'   : m.ewmaStage === 'MID' ? '#f97316'   : '#fbbf24',
                        border: 'none',
                      }}>📡 {m.ewmaStage}</span>
                    )}
                    {m.critCount > 0 && <span className="emc-badge badge-crit">{m.critCount} critical</span>}
                    {m.warnCount > 0 && <span className="emc-badge badge-warn">{m.warnCount} warning</span>}
                    <span className="emc-sensor-count">📡 {m.sensors.length}</span>
                    <span className={`emc-status st-${m.machineStatus.toLowerCase()}`}>
                      ● {m.machineStatus}
                    </span>
                  </div>
                  <span className={`emc-chevron ${isOpen ? 'open' : ''}`}>▸</span>
                </div>

                {isOpen && (
                  <div className="emc-sensors">
                    <div className="ems-header-row">
                      <span className="ems-h">Sensor</span>
                      <span className="ems-h">Type</span>
                      <span className="ems-h">Value</span>
                      <span className="ems-h ems-h-bar">Range</span>
                      <span className="ems-h">Status</span>
                    </div>
                    {m.sensors.map(s => (
                      <div className={`ems-row st-${s.status}`} key={s.sensorId}>
                        <span className="ems-name">{s.name}</span>
                        <span className="ems-type">{s.type}</span>
                        <span className="ems-value">
                          <strong>{s.liveValue.toFixed(1)}</strong>
                          <span className="ems-unit">{s.unit}</span>
                        </span>
                        <div className="ems-bar-wrap">
                          <div className="ems-bar-track">
                            <div className={`ems-bar-fill bar-${s.status}`} style={{ width: `${s.pct}%` }} />
                            <div className="ems-bar-warn-line" style={{ left: '80%' }} />
                            <div className="ems-bar-crit-line" style={{ left: '95%' }} />
                          </div>
                          <div className="ems-bar-labels">
                            <span>{s.min}{s.unit}</span>
                            <span>{s.max}{s.unit}</span>
                          </div>
                        </div>
                        <span className={`ems-status-dot dot-${s.status}`}>
                          {s.status === 'critical' ? '🔴' : s.status === 'warning' ? '🟡' : '🟢'} {s.status.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Table view */}
      {view === 'table' && (
        <div className="ent-table-wrap">
          <table className="ent-table">
            <thead>
              <tr>
                <th>Machine</th>
                <th>Sensor</th>
                <th>Type</th>
                <th>Value</th>
                <th>Min</th>
                <th>Max</th>
                <th>Usage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.flatMap(m =>
                m.sensors.map(s => (
                  <tr key={`${m.machineId}-${s.sensorId}`} className={`tbl-${s.status}`}>
                    <td className="tbl-machine">{m.name}</td>
                    <td>{s.name}</td>
                    <td className="tbl-type">{s.type}</td>
                    <td className="tbl-value"><strong>{s.liveValue.toFixed(1)}</strong> {s.unit}</td>
                    <td className="tbl-dim">{s.min}</td>
                    <td className="tbl-dim">{s.max}</td>
                    <td>
                      <div className="tbl-bar">
                        <div className={`tbl-bar-fill bar-${s.status}`} style={{ width: `${s.pct}%` }} />
                      </div>
                    </td>
                    <td>
                      <span className={`tbl-status-badge st-${s.status}`}>
                        {s.status === 'critical' ? '🔴' : s.status === 'warning' ? '🟡' : '🟢'} {s.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Entities;
